import { array, bool, nullable, num, object, oneOf, optional, str } from 'validation';

// Shared sub-object types + schemas for the recurrence_definitions endpoints.
//
// These are shared by IDENTITY, not coincidence: the backend produces `recurrences`
// via one serializer — `recurrenceDbRowToWire`
// (`modules/generic_functions/recurrence_definitions/recurrenceDefinitionRecurrences.js`)
// — for EVERY endpoint (GET one, GET list, POST, PUT), and `folder_path` via the
// one `#attachFolderPaths` helper. Update the backend serializer first if the wire
// changes, then mirror here. See `../../README.md`. Validated wire data is
// immutable, so the types are deeply readonly.

export type RecurrenceUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' | 'window_open';

const RECURRENCE_UNITS: readonly RecurrenceUnit[] = ['second', 'minute', 'hour', 'day', 'week', 'month', 'year', 'window_open'];

// One recurrence rule. Shared between the read/write responses (GET/POST/PUT) and
// the Save body: the backend round-trips the same field names in both directions.
// Loose at the type level — the backend's `#validateRecurrences()` owns the
// inter-field rules (interval required iff unit !== 'window_open';
// scheduling_window required iff unit === 'window_open'). A too-strict client type
// would block valid sends if the backend ever loosens them.
//
// `optional()` vs `optional(nullable())` is deliberate and load-bearing, derived
// from `recurrenceDbRowToWire`:
// - `description`, `unit`, `preserve_time`, `skip_when_window_closed` are ALWAYS
//   present on a response (description nullable, the rest not).
// - `interval`, `scheduling_window`, `during_scheduling_window`, `start_time`,
//   `start_time_zone` are OMITTED when null — the wire never carries them as an
//   explicit `null`, so `optional()` (absent | value) is the exact contract.
// - `sort_order` / `recurrence_definition_recurrence_id` are server-assigned:
//   present on every response row, absent on a Save body.
//
// `resubmit_on_error` lives on the parent definition, not on individual rules.
//
// `scheduling_window` / `during_scheduling_window` are `zone_name|window_name`
// strings on the wire (the backend resolves them to ids).
export interface Recurrence {
  readonly description?: string | null;
  readonly unit: RecurrenceUnit;
  readonly interval?: number;
  readonly scheduling_window?: string;
  readonly during_scheduling_window?: string;
  // When true (default), occurrences keep the same local wall-clock time across
  // daylight-saving changes. When false, occurrences are spaced by an exact fixed
  // interval, so the local time may shift by an hour across a DST change.
  readonly preserve_time?: boolean;
  readonly skip_when_window_closed?: boolean;
  // Time-of-day pinning (nap-backend #1031). Backend enforces the inter-field
  // rules (start_time format, start_time_zone-requires-start_time, IANA timezone
  // validity).
  readonly start_time?: string;
  readonly start_time_zone?: string;
  // Server-assigned identity/order — present on a response, absent on a Save.
  readonly sort_order?: number;
  readonly recurrence_definition_recurrence_id?: number;
}

export const recurrenceSchema = object<Recurrence>({
  description: optional(nullable(str())),
  unit: oneOf(RECURRENCE_UNITS, 'day'),
  interval: optional(num()),
  scheduling_window: optional(str()),
  during_scheduling_window: optional(str()),
  preserve_time: optional(bool()),
  skip_when_window_closed: optional(bool()),
  start_time: optional(str()),
  start_time_zone: optional(str()),
  sort_order: optional(num()),
  recurrence_definition_recurrence_id: optional(num()),
});

// POST/PUT request body. Shared by both writes because the FE sends the same
// shape to each. Required-by-server fields are typed `T | null`, not strictly
// `T` — the backend's `#validateRecurrences()` and body guards
// (`if (!name || !zone_name)`) own the validity rules; a too-strict client type
// would block valid sends if those rules loosen. The FE authors this body, so
// it is never validated here (the server is the authority; it returns 400 and
// the UI surfaces that).
//
// `recurrences` is required and the backend rejects an empty array; we don't
// re-encode that rule. Each entry reuses the wire `Recurrence`; the
// server-assigned `sort_order` / `recurrence_definition_recurrence_id` are
// omitted on the way out (order is conveyed by array position — the backend
// assigns `sort_order` by index).
export interface SaveRecurrenceDefinition {
  readonly name: string | null;
  readonly description: string | null;
  readonly zone_name: string | null;
  readonly resubmit_on_error?: boolean;
  readonly recurrences: readonly Recurrence[];
  // Folder assignment (nap-backend #1076). Top-level body field; `null` clears
  // the folder. Always sent.
  readonly folder_id: number | null;
}
